import React, { useState, useEffect } from 'react';
import { salesService, catalogoPlanosService } from '../services/api';
import { maskCPF, maskCNPJ, maskPhone, maskCEP, maskMoney, unmaskMoney } from '../utils/masks';
import { parseObservacoesTratamento } from '../utils/observacoesTratamento';
import ProdutosPfEditor, {
  parseProdutosSelecionados,
  somaProdutosPf,
  montarObservacaoProdutos,
  serializarProdutosPf
} from './ProdutosPfEditor';

const getTodayBrasilia = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  background: 'var(--cinza-escuro-2)',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  color: 'var(--texto-primario)'
};

const normalizeVendaForEdit = (venda) => {
  if (!venda) return venda;
  const normalized = {
    ...venda,
    observacao_venda: venda.observacao_venda || '',
    data_instalacao: venda.data_instalacao || venda.data_agendamento1 || getTodayBrasilia(),
    numero_ordem: venda.numero_ordem || ''
  };
  // Para PF, o CPF principal fica em documento; o campo cpf pode estar vazio no banco
  if (normalized.tipo_cliente === 'cpf' && !normalized.cpf && normalized.documento) {
    normalized.cpf = normalized.documento;
  }
  return normalized;
};

const buildUpdatePayload = (editingVenda, extra = {}) => {
  const documento = editingVenda.documento ? editingVenda.documento.replace(/\D/g, '') : '';
  const cpfRaw = editingVenda.cpf ? editingVenda.cpf.replace(/\D/g, '') : null;
  const rawValor = editingVenda.valor_total;
  const valorTotalNormalizado = typeof rawValor === 'string' && /[,\.]/.test(rawValor)
    ? unmaskMoney(rawValor)
    : (parseFloat(String(rawValor).replace(',', '.')) || 0).toFixed(2);
  const payload = {
    ...editingVenda,
    ...extra,
    documento,
    telefone: editingVenda.telefone ? editingVenda.telefone.replace(/\D/g, '') : '',
    telefone2: editingVenda.telefone2 ? editingVenda.telefone2.replace(/\D/g, '') : null,
    cpf: editingVenda.tipo_cliente === 'cpf' ? (cpfRaw || documento || null) : cpfRaw,
    rg: editingVenda.rg || null,
    cep: editingVenda.cep ? editingVenda.cep.replace(/\D/g, '') : '',
    valor_total: valorTotalNormalizado,
    data_agendamento2: editingVenda.data_agendamento2 || null,
    periodo_agendamento2: editingVenda.periodo_agendamento2 || null,
    data_nascimento: editingVenda.data_nascimento || null,
    operadora_viabilidade_json: editingVenda.operadora_viabilidade_json
      ? (typeof editingVenda.operadora_viabilidade_json === 'string'
        ? editingVenda.operadora_viabilidade_json
        : JSON.stringify(editingVenda.operadora_viabilidade_json))
      : null
  };
  // Histórico é append-only no backend; não reenviar o texto completo da venda
  delete payload.observacao_tratamento;
  if (editingVenda.produtos_selecionados_json !== undefined) {
    payload.produtos_selecionados_json = editingVenda.produtos_selecionados_json;
  }
  return payload;
};

const EditarVendaModal = ({
  venda,
  user,
  statusOptions = [],
  modoValidacaoOk = false,
  onClose,
  onSaved,
  stacked = false
}) => {
  const [editingVenda, setEditingVenda] = useState(() => normalizeVendaForEdit(venda));
  const [saving, setSaving] = useState(false);
  const [produtosPfCatalogo, setProdutosPfCatalogo] = useState([]);
  const [produtosSelecionados, setProdutosSelecionados] = useState(() => parseProdutosSelecionados(venda?.produtos_selecionados_json));
  const [buscaProdutoPf, setBuscaProdutoPf] = useState('');
  const [loadingProdutosPf, setLoadingProdutosPf] = useState(false);
  const [produtosPfError, setProdutosPfError] = useState('');
  const [observacaoExtra, setObservacaoExtra] = useState('');
  const [observacaoRetornoBko, setObservacaoRetornoBko] = useState('');

  const podeRetornarAoBko = !modoValidacaoOk
    && (venda?.status === 'pendente_vendedor' || editingVenda?.status === 'pendente_vendedor');

  useEffect(() => {
    const normalized = normalizeVendaForEdit(venda);
    const parsed = parseProdutosSelecionados(venda?.produtos_selecionados_json);
    setProdutosSelecionados(parsed);
    setObservacaoRetornoBko('');
    if (parsed.length > 0) {
      const soma = somaProdutosPf(parsed);
      setEditingVenda({
        ...normalized,
        valor_total: maskMoney(String(Math.round(soma * 100)))
      });
    } else {
      setEditingVenda(normalized);
    }
    if (parsed.length > 0 && venda?.observacao_venda) {
      const prefixo = parsed.map((p) => p.nome_neo).join(' + ');
      const extra = venda.observacao_venda.startsWith(prefixo)
        ? venda.observacao_venda.slice(prefixo.length).replace(/^\.\s*/, '')
        : venda.observacao_venda;
      setObservacaoExtra(extra);
    } else {
      setObservacaoExtra(venda?.observacao_venda || '');
    }
  }, [venda]);

  useEffect(() => {
    if (editingVenda.tipo_cliente !== 'cpf') {
      setProdutosPfCatalogo([]);
      setProdutosSelecionados([]);
      setBuscaProdutoPf('');
      return undefined;
    }
    let cancelled = false;
    const loadPf = async () => {
      try {
        setLoadingProdutosPf(true);
        const data = await catalogoPlanosService.listPfNeo();
        if (!cancelled) setProdutosPfCatalogo(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erro ao carregar produtos PF:', err);
        if (!cancelled) setProdutosPfCatalogo([]);
      } finally {
        if (!cancelled) setLoadingProdutosPf(false);
      }
    };
    loadPf();
    return () => { cancelled = true; };
  }, [editingVenda.tipo_cliente]);

  const recalcularValorTotalProdutos = (lista) => {
    const soma = somaProdutosPf(lista);
    setEditingVenda((prev) => ({
      ...prev,
      valor_total: maskMoney(String(Math.round(soma * 100)))
    }));
  };

  const adicionarProdutoPf = (item) => {
    if (produtosSelecionados.some((p) => p.catalogo_plano_id === item.id)) return;
    const valorPadrao = parseFloat(item.valor_padrao) || 0;
    const novo = {
      catalogo_plano_id: item.id,
      nome_neo: item.nome_neo || item.titulo,
      filtro_busca: item.filtro_busca || null,
      valor_negociado: valorPadrao,
      quantidade: 1
    };
    const novaLista = [...produtosSelecionados, novo];
    setProdutosSelecionados(novaLista);
    recalcularValorTotalProdutos(novaLista);
    setBuscaProdutoPf('');
    setProdutosPfError('');
  };

  const removerProdutoPf = (catalogoPlanoId) => {
    const novaLista = produtosSelecionados.filter((p) => p.catalogo_plano_id !== catalogoPlanoId);
    setProdutosSelecionados(novaLista);
    recalcularValorTotalProdutos(novaLista);
  };

  const atualizarProdutoPf = (catalogoPlanoId, field, value) => {
    const novaLista = produtosSelecionados.map((p) => {
      if (p.catalogo_plano_id !== catalogoPlanoId) return p;
      if (field === 'valor_negociado') {
        const num = typeof value === 'string' ? parseFloat(unmaskMoney(value)) : value;
        return { ...p, valor_negociado: Number.isNaN(num) ? 0 : num };
      }
      if (field === 'quantidade') {
        const q = parseInt(value, 10) || 1;
        return { ...p, quantidade: q < 1 ? 1 : q };
      }
      return p;
    });
    setProdutosSelecionados(novaLista);
    recalcularValorTotalProdutos(novaLista);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (modoValidacaoOk) {
      if (!editingVenda.data_instalacao) {
        alert('O campo Data da Instalação é obrigatório para finalizar Validação OK');
        return;
      }
      if (!editingVenda.numero_ordem || !String(editingVenda.numero_ordem).trim()) {
        alert('O campo Número da Ordem é obrigatório para finalizar Validação OK');
        return;
      }
    }

    if (editingVenda.tipo_cliente === 'cpf' && produtosSelecionados.length > 0) {
      setProdutosPfError('');
    }

    try {
      setSaving(true);
      const usaProdutosPf = editingVenda.tipo_cliente === 'cpf' && produtosSelecionados.length > 0;
      // No retorno ao BKO, o campo de obs é nota nova (histórico); não sobrescreve observacao_venda
      const observacaoFinal = podeRetornarAoBko
        ? (venda?.observacao_venda || '')
        : (usaProdutosPf
          ? montarObservacaoProdutos(produtosSelecionados, observacaoExtra)
          : (editingVenda.observacao_venda || ''));

      const valorTotalFinal = usaProdutosPf
        ? somaProdutosPf(produtosSelecionados)
        : parseFloat(typeof editingVenda.valor_total === 'string'
          ? unmaskMoney(editingVenda.valor_total)
          : String(editingVenda.valor_total)) || 0;

      const vendaParaSalvar = {
        ...editingVenda,
        valor_total: valorTotalFinal.toFixed(2),
        observacao_venda: observacaoFinal,
        produtos_selecionados_json: usaProdutosPf
          ? serializarProdutosPf(produtosSelecionados)
          : null
      };

      const updateData = buildUpdatePayload(vendaParaSalvar, modoValidacaoOk
        ? {
          validacao_ok_apos_edicao: true,
          data_instalacao: editingVenda.data_instalacao,
          numero_ordem: String(editingVenda.numero_ordem).trim()
        }
        : {});

      // Nota nova para o histórico (append no backend); não envia o histórico completo
      if (podeRetornarAoBko && observacaoRetornoBko.trim()) {
        updateData.observacao_tratamento = observacaoRetornoBko.trim();
      }

      await salesService.update(editingVenda.id, updateData);
      alert(modoValidacaoOk ? '✅ Venda editada e finalizada com Validação OK!' : '✅ Venda atualizada com sucesso!');
      onSaved?.();
    } catch (error) {
      console.error('Erro ao atualizar venda:', error);
      alert('❌ Erro ao atualizar venda: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setSaving(false);
    }
  };

  const overlayClass = stacked ? 'modal-overlay modal-overlay--stacked' : 'modal-overlay';

  return (
    <div className={overlayClass} onClick={onClose}>
      <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', maxHeight: '95vh' }}>
        <div className="modal-header-venda">
          <h2>
            {modoValidacaoOk ? `✏️ Editar Venda #${editingVenda.id} — Validação OK` : `✏️ Editar Venda #${editingVenda.id}`}
          </h2>
          <span className="close-modal" onClick={onClose}>×</span>
        </div>

        <form onSubmit={handleSubmit} className="modal-body-venda" style={{ maxHeight: 'calc(95vh - 100px)', overflowY: 'auto' }}>
          {!modoValidacaoOk && (
            <div className="info-section">
              <h4>📊 Status da Venda</h4>
              {['admin', 'diretor'].includes(user?.role) && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Status *</label>
                  <select
                    value={editingVenda.status || 'pendente_bko'}
                    onChange={(e) => setEditingVenda({ ...editingVenda, status: e.target.value })}
                    style={{ ...inputStyle, borderRadius: '8px' }}
                  >
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
              {user?.role === 'vendedor' && editingVenda.status === 'pendente_vendedor' && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Status *</label>
                  <select
                    value={editingVenda.status}
                    onChange={(e) => setEditingVenda({ ...editingVenda, status: e.target.value })}
                    style={{ ...inputStyle, borderRadius: '8px' }}
                  >
                    <option value="pendente_vendedor">Pendente Vendedor</option>
                    <option value="pendente_bko">Pendente BKO</option>
                  </select>
                </div>
              )}
            </div>
          )}

          <div className="info-section">
            <h4>📋 Tipo de Cliente</h4>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Tipo *</div>
                <select
                  value={editingVenda.tipo_cliente || 'cpf'}
                  onChange={(e) => {
                    const tipo = e.target.value;
                    setEditingVenda({ ...editingVenda, tipo_cliente: tipo });
                    if (tipo === 'cnpj') {
                      setProdutosSelecionados([]);
                      setProdutosPfError('');
                    }
                  }}
                  style={inputStyle}
                >
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                </select>
              </div>
              <div className="info-item">
                <div className="info-label">{editingVenda.tipo_cliente === 'cpf' ? 'CPF *' : 'CNPJ *'}</div>
                <input
                  type="text"
                  value={editingVenda.documento ? (editingVenda.tipo_cliente === 'cpf' ? maskCPF(editingVenda.documento) : maskCNPJ(editingVenda.documento)) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditingVenda({
                      ...editingVenda,
                      documento: value,
                      ...(editingVenda.tipo_cliente === 'cpf' ? { cpf: value } : {})
                    });
                  }}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          <div className="info-section">
            <h4>👤 Informações do Cliente</h4>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">{editingVenda.tipo_cliente === 'cnpj' ? 'Razão Social *' : 'Nome Completo *'}</div>
                <input
                  type="text"
                  value={editingVenda.razao_social || editingVenda.nome_cliente || ''}
                  onChange={(e) => setEditingVenda({ ...editingVenda, [editingVenda.tipo_cliente === 'cnpj' ? 'razao_social' : 'nome_cliente']: e.target.value })}
                  style={inputStyle}
                />
              </div>
              {editingVenda.tipo_cliente === 'cnpj' && (
                <div className="info-item">
                  <div className="info-label">Nome do Cliente</div>
                  <input type="text" value={editingVenda.nome_cliente || ''} onChange={(e) => setEditingVenda({ ...editingVenda, nome_cliente: e.target.value })} style={inputStyle} />
                </div>
              )}
              {editingVenda.tipo_cliente === 'cpf' && (
                <>
                  <div className="info-item">
                    <div className="info-label">RG</div>
                    <input type="text" value={editingVenda.rg || ''} onChange={(e) => setEditingVenda({ ...editingVenda, rg: e.target.value })} style={inputStyle} placeholder="Não informado na venda" />
                  </div>
                  <div className="info-item">
                    <div className="info-label">Nome da Mãe</div>
                    <input type="text" value={editingVenda.nome_mae || ''} onChange={(e) => setEditingVenda({ ...editingVenda, nome_mae: e.target.value })} style={inputStyle} />
                  </div>
                  <div className="info-item">
                    <div className="info-label">Data de Nascimento</div>
                    <input type="date" value={editingVenda.data_nascimento || ''} onChange={(e) => setEditingVenda({ ...editingVenda, data_nascimento: e.target.value })} style={inputStyle} />
                  </div>
                </>
              )}
              {editingVenda.tipo_cliente === 'cnpj' && (
                <>
                  <div className="info-item">
                    <div className="info-label">Estado do CNPJ</div>
                    <input type="text" value={editingVenda.estado_cnpj || ''} onChange={(e) => setEditingVenda({ ...editingVenda, estado_cnpj: e.target.value })} maxLength={2} style={inputStyle} />
                  </div>
                  <div className="info-item">
                    <div className="info-label">Nome do Representante</div>
                    <input type="text" value={editingVenda.nome_representante || ''} onChange={(e) => setEditingVenda({ ...editingVenda, nome_representante: e.target.value })} style={inputStyle} />
                  </div>
                  <div className="info-item">
                    <div className="info-label">CPF do Representante</div>
                    <input type="text" value={editingVenda.cpf ? maskCPF(editingVenda.cpf) : ''} onChange={(e) => setEditingVenda({ ...editingVenda, cpf: e.target.value })} style={inputStyle} />
                  </div>
                  <div className="info-item">
                    <div className="info-label">RG do Representante</div>
                    <input type="text" value={editingVenda.rg || ''} onChange={(e) => setEditingVenda({ ...editingVenda, rg: e.target.value })} style={inputStyle} />
                  </div>
                </>
              )}
              <div className="info-item">
                <div className="info-label">Telefone 1 *</div>
                <input type="text" value={editingVenda.telefone ? maskPhone(editingVenda.telefone) : ''} onChange={(e) => setEditingVenda({ ...editingVenda, telefone: e.target.value })} style={inputStyle} />
              </div>
              <div className="info-item">
                <div className="info-label">Telefone 2</div>
                <input type="text" value={editingVenda.telefone2 ? maskPhone(editingVenda.telefone2) : ''} onChange={(e) => setEditingVenda({ ...editingVenda, telefone2: e.target.value })} style={inputStyle} />
              </div>
              <div className="info-item">
                <div className="info-label">Email</div>
                <input type="email" value={editingVenda.email || ''} onChange={(e) => setEditingVenda({ ...editingVenda, email: e.target.value })} style={inputStyle} />
              </div>
            </div>
          </div>

          <div className="info-section">
            <h4>📍 Endereço</h4>
            <div className="info-grid">
              {['cep', 'logradouro', 'numero', 'bairro', 'cidade'].map((field) => (
                <div className="info-item" key={field}>
                  <div className="info-label">{field === 'cep' ? 'CEP *' : field === 'numero' ? 'Número *' : field.charAt(0).toUpperCase() + field.slice(1) + ' *'}</div>
                  <input
                    type="text"
                    value={field === 'cep' && editingVenda.cep ? maskCEP(editingVenda.cep) : (editingVenda[field] || '')}
                    onChange={(e) => setEditingVenda({ ...editingVenda, [field]: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              ))}
              <div className="info-item">
                <div className="info-label">Estado *</div>
                <input type="text" value={editingVenda.estado || ''} maxLength={2} onChange={(e) => setEditingVenda({ ...editingVenda, estado: e.target.value.toUpperCase() })} style={inputStyle} />
              </div>
              <div className="info-item">
                <div className="info-label">Estado Completo *</div>
                <input type="text" value={editingVenda.estado_completo || ''} onChange={(e) => setEditingVenda({ ...editingVenda, estado_completo: e.target.value })} style={inputStyle} />
              </div>
              <div className="info-item">
                <div className="info-label">Complemento 1</div>
                <input type="text" value={editingVenda.complemento1 || ''} onChange={(e) => setEditingVenda({ ...editingVenda, complemento1: e.target.value })} style={inputStyle} />
              </div>
              <div className="info-item">
                <div className="info-label">Complemento 2</div>
                <input type="text" value={editingVenda.complemento2 || ''} onChange={(e) => setEditingVenda({ ...editingVenda, complemento2: e.target.value })} style={inputStyle} />
              </div>
              <div className="info-item">
                <div className="info-label">Complemento 3</div>
                <input type="text" value={editingVenda.complemento3 || ''} onChange={(e) => setEditingVenda({ ...editingVenda, complemento3: e.target.value })} style={inputStyle} />
              </div>
            </div>
          </div>

          <div className="info-section">
            <h4>📅 Agendamento de Instalação</h4>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Data Opção 1 *</div>
                <input type="date" value={editingVenda.data_agendamento1 || ''} onChange={(e) => setEditingVenda({ ...editingVenda, data_agendamento1: e.target.value })} style={inputStyle} />
              </div>
              <div className="info-item">
                <div className="info-label">Período Opção 1 *</div>
                <select value={editingVenda.periodo_agendamento1 || 'manha'} onChange={(e) => setEditingVenda({ ...editingVenda, periodo_agendamento1: e.target.value })} style={inputStyle}>
                  <option value="manha">Manhã</option>
                  <option value="tarde">Tarde</option>
                  <option value="noite">Noite</option>
                </select>
              </div>
              <div className="info-item">
                <div className="info-label">Data Opção 2</div>
                <input type="date" value={editingVenda.data_agendamento2 || ''} onChange={(e) => setEditingVenda({ ...editingVenda, data_agendamento2: e.target.value || null })} style={inputStyle} />
              </div>
              <div className="info-item">
                <div className="info-label">Período Opção 2</div>
                <select value={editingVenda.periodo_agendamento2 || ''} onChange={(e) => setEditingVenda({ ...editingVenda, periodo_agendamento2: e.target.value || null })} style={inputStyle}>
                  <option value="">Selecione...</option>
                  <option value="manha">Manhã</option>
                  <option value="tarde">Tarde</option>
                  <option value="noite">Noite</option>
                </select>
              </div>
              <div className="info-item">
                <div className="info-label">Aceita Antecipação</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editingVenda.aceita_antecipacao || false} onChange={(e) => setEditingVenda({ ...editingVenda, aceita_antecipacao: e.target.checked })} style={{ width: 'auto' }} />
                  <span>Sim</span>
                </label>
              </div>
            </div>
          </div>

          {editingVenda.tipo_cliente === 'cpf' && (
            <ProdutosPfEditor
              produtosPfCatalogo={produtosPfCatalogo}
              produtosSelecionados={produtosSelecionados}
              buscaProdutoPf={buscaProdutoPf}
              loadingProdutosPf={loadingProdutosPf}
              error={produtosPfError}
              onBuscaChange={setBuscaProdutoPf}
              onAdicionar={adicionarProdutoPf}
              onRemover={removerProdutoPf}
              onAtualizar={atualizarProdutoPf}
            />
          )}

          <div className="info-section">
            <h4>💰 Informações Financeiras</h4>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Valor Total (R$) *</div>
                {(user?.role === 'backoffice' || user?.role === 'supervisor_bko') && editingVenda.status === 'concluido' && !modoValidacaoOk
                  ? <div className="info-value" style={{ padding: '0.75rem', color: 'var(--texto-secundario)' }}>—</div>
                  : (
                    <input
                      type="text"
                      value={editingVenda.valor_total ? (typeof editingVenda.valor_total === 'number' ? maskMoney((editingVenda.valor_total * 100).toString()) : editingVenda.valor_total) : ''}
                      onChange={(e) => setEditingVenda({ ...editingVenda, valor_total: e.target.value })}
                      readOnly={editingVenda.tipo_cliente === 'cpf' && produtosSelecionados.length > 0}
                      style={inputStyle}
                    />
                  )}
              </div>
              <div className="info-item">
                <div className="info-label">Data de Vencimento *</div>
                <select value={editingVenda.data_vencimento || 10} onChange={(e) => setEditingVenda({ ...editingVenda, data_vencimento: parseInt(e.target.value, 10) })} style={inputStyle}>
                  <option value={1}>Dia 1</option>
                  <option value={10}>Dia 10</option>
                  <option value={17}>Dia 17</option>
                  <option value={21}>Dia 21</option>
                  <option value={26}>Dia 26</option>
                </select>
              </div>
            </div>
          </div>

          {modoValidacaoOk && (
            <div className="info-section">
              <h4>📋 Dados PAP</h4>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Código PAP</div>
                  <input type="text" value={editingVenda.vendedor_codigo_pap || ''} onChange={(e) => setEditingVenda({ ...editingVenda, vendedor_codigo_pap: e.target.value })} style={inputStyle} />
                </div>
                <div className="info-item">
                  <div className="info-label">Login PAP</div>
                  <input type="text" value={editingVenda.vendedor_login_pap || ''} onChange={(e) => setEditingVenda({ ...editingVenda, vendedor_login_pap: e.target.value })} style={inputStyle} />
                </div>
                <div className="info-item">
                  <div className="info-label">Número do Pedido PAP</div>
                  <input type="text" value={editingVenda.numero_pedido_pap || ''} onChange={(e) => setEditingVenda({ ...editingVenda, numero_pedido_pap: e.target.value })} style={inputStyle} />
                </div>
              </div>
            </div>
          )}

          {editingVenda.observacao_tratamento && (
            <div className="info-section">
              <h4>📋 Histórico de Tratamento e Mudanças de Status</h4>
              <div
                className="historico-tratamento-container"
                style={{
                  maxHeight: '250px',
                  overflowY: 'auto',
                  background: 'var(--cinza-escuro-2)',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}
              >
                {parseObservacoesTratamento(editingVenda.observacao_tratamento).map((obs, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: '0.75rem',
                      padding: '0.75rem',
                      background: 'var(--bg-primary)',
                      borderRadius: '6px',
                      borderLeft: obs.tipo === 'mudanca_status' ? '4px solid var(--azul-dvs)' : '4px solid #ff9800'
                    }}
                  >
                    {obs.tipo === 'mudanca_status' ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '1rem' }}>🔄</span>
                          <span style={{ fontWeight: '600', color: 'var(--azul-dvs)', fontSize: '0.9rem' }}>Mudança de Status</span>
                          {obs.dataHora && (
                            <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--texto-secundario)' }}>
                              {obs.dataHora}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{
                            padding: '0.3rem 0.6rem',
                            background: 'rgba(255, 152, 0, 0.2)',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            color: '#ff9800'
                          }}>
                            {obs.statusAnterior}
                          </span>
                          <span style={{ color: 'var(--texto-secundario)' }}>→</span>
                          <span style={{
                            padding: '0.3rem 0.6rem',
                            background: 'rgba(6, 102, 255, 0.2)',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            color: 'var(--azul-dvs)',
                            fontWeight: '600'
                          }}>
                            {obs.statusNovo}
                          </span>
                        </div>
                        {obs.usuario && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--texto-secundario)' }}>
                            Por: <strong>{obs.usuario}</strong>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '1rem' }}>💬</span>
                          <span style={{ fontWeight: '600', color: '#ff9800', fontSize: '0.9rem' }}>Observação</span>
                          {obs.dataHora && (
                            <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--texto-secundario)' }}>
                              {obs.dataHora}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--texto-primario)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                          {obs.texto}
                        </div>
                        {obs.usuario && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--texto-secundario)' }}>
                            Por: <strong>{obs.usuario}</strong>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {parseObservacoesTratamento(editingVenda.observacao_tratamento).length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--texto-secundario)', padding: '1rem' }}>
                    Nenhum registro encontrado
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="info-section">
            <h4>📝 Observações da Venda</h4>
            <div className="form-group">
              {podeRetornarAoBko ? (
                <>
                  <label>Observação da Venda</label>
                  <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginBottom: '0.5rem' }}>
                    Digite uma nova observação. Ela será adicionada ao histórico e a venda voltará para Pendente BKO.
                  </p>
                  <textarea
                    value={observacaoRetornoBko}
                    onChange={(e) => setObservacaoRetornoBko(e.target.value)}
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(59, 130, 246, 0.10)',
                      border: '1px solid rgba(59, 130, 246, 0.55)',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.12)',
                      borderRadius: '8px',
                      color: 'var(--texto-primario)',
                      fontFamily: 'inherit'
                    }}
                    placeholder="digite sua observação aqui para o bko"
                  />
                </>
              ) : (
                <>
                  <label>{editingVenda.tipo_cliente === 'cpf' && produtosSelecionados.length > 0 ? 'Observação adicional (opcional)' : 'Observação da Venda'}</label>
                  <textarea
                    value={editingVenda.tipo_cliente === 'cpf' && produtosSelecionados.length > 0 ? observacaoExtra : (editingVenda.observacao_venda || '')}
                    onChange={(e) => {
                      if (editingVenda.tipo_cliente === 'cpf' && produtosSelecionados.length > 0) {
                        setObservacaoExtra(e.target.value);
                      } else {
                        setEditingVenda({ ...editingVenda, observacao_venda: e.target.value });
                      }
                    }}
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(59, 130, 246, 0.10)',
                      border: '1px solid rgba(59, 130, 246, 0.55)',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.12)',
                      borderRadius: '8px',
                      color: 'var(--texto-primario)',
                      fontFamily: 'inherit'
                    }}
                    placeholder={editingVenda.tipo_cliente === 'cpf'
                      ? 'Texto extra além dos produtos PF selecionados...'
                      : 'Digite a observação da venda...'}
                  />
                  {editingVenda.tipo_cliente === 'cpf' && produtosSelecionados.length > 0 && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
                      Produtos: {produtosSelecionados.map((p) => p.nome_neo).join(' + ')}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {(editingVenda.operadora_selecionada || editingVenda.operadora_viabilidade_json) && (
            <div className="info-section">
              <h4>📡 Informações de Viabilidade</h4>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Operadora Selecionada</div>
                  <input type="text" value={editingVenda.operadora_selecionada || ''} onChange={(e) => setEditingVenda({ ...editingVenda, operadora_selecionada: e.target.value })} style={inputStyle} />
                </div>
              </div>
            </div>
          )}

          {modoValidacaoOk && (
            <div className="info-section">
              <h4>🔧 Ordem e Instalação</h4>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Data da Instalação *</div>
                  <input
                    type="date"
                    value={editingVenda.data_instalacao || ''}
                    onChange={(e) => setEditingVenda({ ...editingVenda, data_instalacao: e.target.value })}
                    required
                    style={inputStyle}
                  />
                </div>
                <div className="info-item">
                  <div className="info-label">Número da Ordem *</div>
                  <input
                    type="text"
                    value={editingVenda.numero_ordem || ''}
                    onChange={(e) => setEditingVenda({ ...editingVenda, numero_ordem: e.target.value })}
                    placeholder="Ex: 123456"
                    required
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? '⏳ Salvando...' : (modoValidacaoOk ? '✅ Salvar e finalizar Validação OK' : '✅ Salvar Alterações')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditarVendaModal;
